import "./index.css";
import { ArrowLeftIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Breadcrumbs } from "@/components/PageHeader/Breadcrumbs";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import { useAppStore } from "@/store/app/slice";
import type { IButtonProps } from "@/ui-kit";
import { Heading, IconButton, Skeleton, Tooltip } from "@/ui-kit";
import { usePageTitleState } from "./PageTitleProvider";

type IProps = {
  title?: ReactNode;
  backUrl?: string;
  backUrlButtonProps?: IButtonProps;
  loading?: boolean;
};

export const PageHeader = ({
  title,
  backUrl,
  backUrlButtonProps,
  loading,
}: IProps) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const navigate = useNavigate();
  const { items } = usePageTitleState();

  const hasContextItems = items.length > 0;

  const contextBackUrl = [...items]
    .sort((a, b) => b.order - a.order)
    .find((item) => item.backUrl)?.backUrl;

  const allLoading = hasContextItems && items.every((item) => item.loading);

  const effectiveBackUrl = hasContextItems ? contextBackUrl : backUrl;
  const effectiveLoading = hasContextItems ? allLoading : loading;

  const handleBackClick = useCallback(() => {
    if (effectiveBackUrl) {
      if (effectiveBackUrl.includes("..")) {
        navigate(effectiveBackUrl, { relative: "path" });
      } else {
        navigate(effectiveBackUrl);
      }
      useAppStore.getState().setSavedUrl(null);
    }
  }, [navigate, effectiveBackUrl]);

  const renderContent = () => {
    if (hasContextItems) {
      return <Breadcrumbs items={items} />;
    }

    if (loading) {
      return <Skeleton width="12.5rem" height="2rem" />;
    }

    return (
      <Heading level={isMobile ? 4 : 3} tone="default" as="h3">
        {title}
      </Heading>
    );
  };

  return (
    <div className="page-header">
      {!effectiveLoading && effectiveBackUrl && (
        <Tooltip content={t("action.back")} placement="bottom-start">
          <IconButton
            className="page-header__back-button"
            content={
              <ArrowLeftIcon className="page-header__back-button-icon" />
            }
            variant="ghost"
            color="neutral"
            aria-label={t("action.back")}
            {...backUrlButtonProps}
            onClick={handleBackClick}
          />
        </Tooltip>
      )}
      {renderContent()}
    </div>
  );
};
